import versionJson from './version.json'
import {
  createBrowserRouter,
  type RouteObject,
  RouterProvider
} from 'react-router'
import SamplesScreen from './screens/SamplesScreen'
import UploadReportsScreen from './screens/UploadReportsScreen'
import { BASE_URL } from './constants'
import {
  AuthProvider,
  ConfirmUpdateModal,
  ErrorScreen,
  LoadingScreen,
  LoginScreen,
  PrivateOutlet,
  RouteMetadata,
  RoleGuard,
  AccessDeniedScreen,
  ThemeProvider,
  useAutoUpdater
} from 'prove-common'
import SettingsScreen from './screens/SettingsScreen'
import { AppLogo } from './components/app-logo/AppLogo'
import { CombinationsScreen } from './screens/CombinationsScreen'
import { CombinationDetailScreen } from './screens/CombinationDetailScreen'
import { RedirectRequest } from '@azure/msal-browser'

const routes: RouteObject[] = [
  {
    element: (
      <RouteMetadata appName='Prøvelab' fallbackTitle='Page not found' />
    ),
    errorElement: (
      <RouteMetadata appName='Prøvelab' fallbackTitle='Page not found'>
        <ErrorScreen
          logo={<AppLogo />}
          defaultErrorTitle='Error'
          defaultErrorMessage='Something went wrong'
        />
      </RouteMetadata>
    ),
    children: [
      {
        path: '/login',
        handle: { title: 'Log in' },
        element: (
          <LoginScreen
            logo={<AppLogo />}
            loginButtonText={'Log in'}
            homeUrl='/samples'
          />
        )
      },
      {
        path: '/unauthorized',
        handle: { title: 'Access denied' },
        element: (
          <AccessDeniedScreen
            title='Access denied'
            description='You do not have access to this content.'
            requestAccessMessage='To request access, contact your manager or support.'
            showAccessInformation={false}
          />
        )
      },
      {
        element: (
          <PrivateOutlet
            homeUrl='/samples'
            loadingElement={
              <LoadingScreen logo={<AppLogo />} loadingMessage='Loading...' />
            }
            errorElement={
              <ErrorScreen
                logo={<AppLogo />}
                defaultErrorTitle='Error'
                defaultErrorMessage='Something went wrong'
              />
            }
          />
        ),
        path: '/',
        children: [
          {
            element: <RoleGuard requiredRoles={['lab-only', 'admin']} />,
            children: [
              {
                path: '/settings',
                handle: { title: 'Settings' },
                element: <SettingsScreen />
              },
              {
                path: '/samples',
                handle: { title: 'Samples' },
                element: <SamplesScreen />
              },
              {
                path: '/combinations',
                handle: { title: 'Combinations' },
                element: <CombinationsScreen />
              },
              {
                path: '/combinations/:analysisTypeId',
                handle: { title: 'Combination details' },
                element: <CombinationDetailScreen />
              },
              {
                path: '/upload-reports',
                handle: { title: 'Upload reports' },
                element: <UploadReportsScreen />
              }
            ]
          }
        ]
      }
    ]
  }
]
const browserRouter = createBrowserRouter(routes)

export default function App({
  loginRequest
}: Readonly<{ loginRequest: RedirectRequest }>) {
  const { updateAvailable } = useAutoUpdater(versionJson)
  return (
    <AuthProvider loginRequest={loginRequest} baseUrl={BASE_URL}>
      <ThemeProvider>
        <RouterProvider router={browserRouter} />
        <ConfirmUpdateModal
          isOpen={updateAvailable}
          heading='New update available'
          description='A new version of the application is available.'
          buttonLabel='Update now'
        />
      </ThemeProvider>
    </AuthProvider>
  )
}
